import { useEffect, useState } from 'preact/hooks';
import { ADMIN_URL, NAV_LINKS } from '../data/content';
import { useWidgetSession } from '../hooks/useWidgetSession';
import './Nav.scss';

/**
 * Sticky nav. Wordmark + live-pulse dot. Past ~32px scroll the inner
 * pill blurs in to keep contrast with whatever's behind it. On mobile
 * (<780px) the links + actions collapse behind a hamburger that opens
 * a glass-panel overlay with staggered link reveals.
 */
export function Nav() {
	const [compact, setCompact] = useState(false);
	const [menuOpen, setMenuOpen] = useState(false);
	// If the visitor is already signed in to PageLoop (widget or admin
	// session on this origin), swap "Sign in" → "Dashboard".
	const session = useWidgetSession();

	useEffect(() => {
		const root = document.documentElement;
		const onScroll = () => {
			setCompact(window.scrollY > 32);
			// Publish 0..1 scroll progress for the top reading-progress bar.
			const max = root.scrollHeight - root.clientHeight;
			root.style.setProperty('--m-scroll', max > 0 ? (window.scrollY / max).toFixed(4) : '0');
		};
		onScroll();
		window.addEventListener('scroll', onScroll, { passive: true });
		window.addEventListener('resize', onScroll, { passive: true });
		return () => {
			window.removeEventListener('scroll', onScroll);
			window.removeEventListener('resize', onScroll);
		};
	}, []);

	/* Lock body scroll while menu is open + Esc to close. */
	useEffect(() => {
		if (!menuOpen) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setMenuOpen(false);
		};
		const prev = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		window.addEventListener('keydown', onKey);
		return () => {
			document.body.style.overflow = prev;
			window.removeEventListener('keydown', onKey);
		};
	}, [menuOpen]);

	const close = () => setMenuOpen(false);

	return (
		<header class={`m-nav ${compact ? 'm-nav--compact' : ''} ${menuOpen ? 'm-nav--open' : ''}`}>
			<div class="m-nav__inner">
				<a class="m-nav__brand" href="/" aria-label="pageloop home">
					<span class="m-nav__mark" aria-hidden="true">
						<span class="m-nav__mark-ring" />
						<span class="m-nav__mark-dot" />
					</span>
					<span class="m-nav__wordmark"><span class="m-nav__wordmark-head">page</span><span class="m-nav__wordmark-tail">loop</span></span>
				</a>

				<nav class="m-nav__links" aria-label="Primary">
					{NAV_LINKS.map((link) => (
						<a key={link.href} class="m-nav__link" href={link.href}>
							{link.label}
						</a>
					))}
				</nav>

				<div class="m-nav__actions">
					{session ? (
						<a
							class="m-nav__signin"
							href={ADMIN_URL}
							title={session.email ? `Signed in as ${session.email}` : undefined}
						>
							Dashboard
						</a>
					) : (
						<a class="m-nav__signin" href={ADMIN_URL}>
							Sign in
						</a>
					)}
					<a class="m-btn m-btn--primary m-nav__cta" href={ADMIN_URL}>
						Get started
					</a>
				</div>

				<button
					class="m-nav__burger"
					type="button"
					aria-label={menuOpen ? 'Close menu' : 'Open menu'}
					aria-expanded={menuOpen}
					aria-controls="m-nav-mobile"
					onClick={() => setMenuOpen((v) => !v)}
				>
					<span class="m-nav__burger-bar" />
					<span class="m-nav__burger-bar" />
					<span class="m-nav__burger-bar" />
				</button>
			</div>

			<span class="m-nav__progress" aria-hidden="true" />

			<div
				class="m-nav__mobile"
				id="m-nav-mobile"
				role="dialog"
				aria-modal="true"
				aria-hidden={!menuOpen}
			>
				<button
					class="m-nav__mobile-scrim"
					type="button"
					aria-label="Close menu"
					tabIndex={menuOpen ? 0 : -1}
					onClick={close}
				/>
				<div class="m-nav__mobile-panel">
					<nav class="m-nav__mobile-links" aria-label="Mobile primary">
						{NAV_LINKS.map((link, i) => (
							<a
								key={link.href}
								class="m-nav__mobile-link"
								href={link.href}
								style={`--m-i:${i}`}
								onClick={close}
							>
								<span class="m-nav__mobile-link-label">{link.label}</span>
								<span class="m-nav__mobile-link-arrow" aria-hidden="true" />
							</a>
						))}
					</nav>

					<div class="m-nav__mobile-actions">
						<a
							class="m-nav__mobile-signin"
							href={ADMIN_URL}
							style={`--m-i:${NAV_LINKS.length}`}
							onClick={close}
						>
							{session ? 'Dashboard' : 'Sign in'}
						</a>
						<a
							class="m-btn m-btn--primary m-nav__mobile-cta"
							href={ADMIN_URL}
							style={`--m-i:${NAV_LINKS.length + 1}`}
							onClick={close}
						>
							Get started
						</a>
					</div>

					<div class="m-nav__mobile-foot">
						<span class="m-nav__mobile-foot-dot" />
						<span>real-time · hosted · drop-in</span>
					</div>
				</div>
			</div>
		</header>
	);
}
